import React, { Component } from 'react';
import moment from 'moment';
import type { InterstitialPageQuery } from '../../../../graphql/pages/interstitial.generated';
import type { Locale } from '../../../../lib/locale';
import type { UserData } from '../../../../lib/data-fetching';
import {
  DL_AD_BANNER,
  DL_OFFERS,
  DL_SHOPS,
  DOM_INTERSTITIAL_PAGE,
  INTERSTITIAL_TIMEOUT,
} from '../../../../lib/constants';
import {
  makeExternalUrl,
  trackAnalytics,
  extensionRedirect,
} from '../../../../lib/utils';
import MaxWidthLayout from '../../../templates/MaxWidthLayout';
import MediaQueryHelper from '../../../templates/MediaQueryHelper';
import MilesOffer from '../shared/MilesOffer';
import { saveInterrupt, removeInterruptById } from '../../../../lib/user';
import {
  getInitialInterruptState,
  getSavedInterruptShop,
} from '../../../../lib/helpers';

import {
  addObjToDataLayer,
  dataLayerIdGenerator,
} from '../../../../lib/dataLayerUtils';
import ShopsLogoContainer from '../shared/ShopsLogoContainer';
import DotsLoader from '../DotsLoader/DotsLoader';
import {
  ActionContainer,
  DesktopExitHelpTextH2,
  DesktopExitTextH1,
  DesktopLogoImgContainer,
  DesktopShopLogoImgContainer,
  GenericImg,
  InterstitialContainerDiv,
  MobileExitHelpTextH2,
  MobileExitTextH1,
  MobileLogoImgContainer,
  MobileShopLogoImgContainer,
} from './interstitialPage.styles';
import InterstitialModal from './interstitialModal/InterstitialModal';
import Button from '../Button/Button';

type QueryItem<T extends Array<unknown> | null> = NonNullable<T>[number];
type InterstitialProps = {
  locale: Locale;
  interstitialData: Partial<NonNullable<InterstitialPageQuery['allInterstitial']>>;
  shopData: Partial<NonNullable<QueryItem<InterstitialPageQuery['shop']>>>;
  offerData: Partial<NonNullable<QueryItem<InterstitialPageQuery['offer']>>>;
  adData: Partial<NonNullable<QueryItem<InterstitialPageQuery['ad']>>>;
  userData: UserData;
  redirectUri?: string;
  redirectUrl?: string;
  auth: boolean;
  environment: string;
};

type InterstitialState = { showInterruptMessage: boolean; hasSavedInterruptPreference: boolean };
type CommissionJunction = { navigate: (url: string | undefined) => void };
declare const cj: CommissionJunction | undefined;

class InterstitialPage extends Component<InterstitialProps, InterstitialState> {
  constructor(props: InterstitialProps) {
    super(props);
    this.state = getInitialInterruptState({ shopData: props.shopData, userData: props.userData });
  }

  componentDidMount() {
    const { shopData: originalShopData, offerData: originalOfferData, adData: originalAdData, auth } = this.props;
    const { showInterruptMessage: isInterruptShown } = this.state;
    const shopData = { ...originalShopData };
    const offerData = { ...originalOfferData };
    const adData = { ...originalAdData };

    const shopSlug = shopData && shopData.slug ? shopData.slug : '';
    const offerSlug = offerData && offerData.slug ? offerData.slug : '';
    const adSlug = adData && adData.slug ? adData.slug : '';

    // Add shop data to dataLayer
    Object.assign(shopData, { dataLayerId: dataLayerIdGenerator(DL_SHOPS, shopSlug) });
    addObjToDataLayer(shopData, 'redirectShop');

    // Add offer data to dataLayer
    Object.assign(offerData, { dataLayerId: dataLayerIdGenerator(DL_OFFERS, offerSlug) });
    addObjToDataLayer(offerData, 'redirectOffer');

    // Add ad data to dataLayer
    Object.assign(adData, { dataLayerId: dataLayerIdGenerator(DL_AD_BANNER, adSlug) });
    addObjToDataLayer(adData, 'redirectAd');

    if (!isInterruptShown) {
      setTimeout(() => this.redirectToExternalLink(), INTERSTITIAL_TIMEOUT);
    }

    // Reload opener if user just logged in
    const targetWindow: unknown = window.opener;
    if (auth && typeof targetWindow === 'object' && targetWindow !== null && 'location' in targetWindow) {
      const { location } = targetWindow;
      if (typeof location === 'object' && location !== null && 'reload' in location
        && typeof location.reload === 'function') {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call -- The opener's reload method was checked above.
        location.reload();
      }
    }
  }

  componentDidUpdate(_previousProps: InterstitialProps, previousState: InterstitialState) {
    const { showInterruptMessage: isInterruptShown } = this.state;
    if (
      previousState.showInterruptMessage !== isInterruptShown
    ) {
      setTimeout(() => this.redirectToExternalLink(), INTERSTITIAL_TIMEOUT);
    }
  }

  redirectToExternalLink = () => {
    const { shopData, redirectUri, environment } = this.props;
    const brokerName = shopData && shopData.broker && shopData.broker.name;

    // Drop cookie to notify browser extension
    extensionRedirect('slug' in shopData ? shopData.slug : undefined, environment);

    // Commission Junction specific redirect method
    if (typeof cj !== 'undefined' && brokerName === 'CJ Affiliate') {
      cj.navigate(redirectUri);
    } else {
      window.location.assign(String(redirectUri));
    }
  };

  render() {
    const { interstitialData, shopData, userData, locale, redirectUrl } = this.props;

    const {
      slug,
      tags,
      shopsLogoText,
      exitText,
      exitHelpText,
      exitButtonText,
    } = interstitialData;

    const {
      id,
      displayName,
      mediumImageUrl,
      mediumImageAltText,
      baseMiles,
      amountPerBaseMiles,
      multiplier,
      siteWideMultiplier,
      config: shopConfig,
      importantInformation,
    } = shopData;

    const {
      singularMilesText = '',
      pluralMilesText = '',
      singularFlatRateMilesText = '',
      pluralFlatRateMilesText = '',
      milesBadgeText = '',
      interruptVisibilityMessage = '',
      interruptContinueButtonText = '',
      interruptHeaderText = '',
    } = shopConfig || {};

    const resolvedShopLogoUrl = String(makeExternalUrl(mediumImageUrl));

    const track = trackAnalytics(
      DOM_INTERSTITIAL_PAGE,
      slug,
      redirectUrl,
      tags,
    );
    const dataTrack = typeof track.dataTrack === 'string' ? track.dataTrack : undefined;

    // Click handler for interrupt preference checkbox
    const toggleInterrupt = async () => {
      if (!id) return;
      const { hasSavedInterruptPreference } = this.state;
      try {
        if (hasSavedInterruptPreference) {
          await removeInterruptById(id);
        } else {
          await saveInterrupt(id, moment().toISOString());
        }
        this.setState((state) => ({
          hasSavedInterruptPreference: !state.hasSavedInterruptPreference,
        }));
      } catch (error) {
        console.error('Unable to update interrupt preference:', error);
      }
    };

    const { showInterruptMessage: isInterruptShown, hasSavedInterruptPreference } = this.state;

    // Click handler for modal actions
    const resumeJourney = async () => {
      if (id && hasSavedInterruptPreference && getSavedInterruptShop(id, userData)) {
        try {
          await saveInterrupt(id, moment().toISOString());
        } catch (error) {
          console.error('Unable to acknowledge updated shop information:', error);
        }
      }
      this.setState({ showInterruptMessage: false });
    };

    const handleResumeJourney = () => {
      resumeJourney().catch((error: unknown) => {
        console.error('Unable to resume shop journey:', error);
      });
    };

    // React ignores the existing async handler result.
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    const handleInterruptChange: React.ChangeEventHandler<HTMLInputElement> = toggleInterrupt;

    const ActionButtons = (
      <ActionContainer>
        <div className="action-checkbox-container">
          <input
            type="checkbox"
            name="checkall"
            id="checkall"
            checked={hasSavedInterruptPreference}
            onChange={handleInterruptChange}
          />
          <label htmlFor="checkall">{interruptVisibilityMessage}</label>
        </div>
        <div className="action-buttons-container">
          <Button
            variant="primary"
            size="medium"
            id={track.dataTrackingId}
            dataTrackingId={track.dataTrackingId}
            dataTrack={dataTrack}
            onClick={handleResumeJourney}
            buttonText={(interruptContinueButtonText || '').replace('%s', displayName || '')}
          />
        </div>
      </ActionContainer>
    );

    const desktopInterstitialPage = (
      <InterstitialContainerDiv>
        <DesktopLogoImgContainer>
          <ShopsLogoContainer
            shopsLogoNavSlot={{ text: shopsLogoText }}
            size="large"
            locale={locale}
          />
        </DesktopLogoImgContainer>

        <DesktopExitTextH1>
          {' '}
          {exitText}
          {displayName}
        </DesktopExitTextH1>

        <DesktopExitHelpTextH2>{exitHelpText}</DesktopExitHelpTextH2>

        <DesktopShopLogoImgContainer>
          <GenericImg src={resolvedShopLogoUrl} alt={mediumImageAltText || ''} />
        </DesktopShopLogoImgContainer>

        <InterstitialModal
          actionButtons={ActionButtons}
          showModal={isInterruptShown}
          closeHandler={handleResumeJourney}
        >
          <h1 className="modal-header-text">{interruptHeaderText}</h1>
          {/* eslint-disable react/no-danger -- CMS supplies the shop's formatted information. */}
          <div
            dangerouslySetInnerHTML={{
              __html: importantInformation || '',
            }}
          />
          {/* eslint-enable react/no-danger */}
        </InterstitialModal>
        <MilesOffer
          className="interstitial"
          multiplier={multiplier}
          siteWideMultiplier={siteWideMultiplier}
          baseMiles={baseMiles}
          amountPerBaseMiles={amountPerBaseMiles}
          singularMilesText={singularMilesText}
          pluralMilesText={pluralMilesText}
          singularFlatRateMilesText={singularFlatRateMilesText}
          pluralFlatRateMilesText={pluralFlatRateMilesText}
          milesBadgeText={milesBadgeText}
          locale={locale}
          size="2xl"
        />
        <DotsLoader />

        <Button
          variant="primary"
          size="medium"
          gutter={9}
          id={track.dataTrackingId}
          data-track-id={track.dataTrackingId}
          data-track-click={dataTrack}
          onClick={this.redirectToExternalLink}
          buttonText={exitButtonText}
        />
      </InterstitialContainerDiv>
    );

    const mobileInterstitialPage = (
      <InterstitialContainerDiv>
        <MobileLogoImgContainer>
          <ShopsLogoContainer
            shopsLogoNavSlot={{ text: shopsLogoText }}
          />
        </MobileLogoImgContainer>

        <MobileExitTextH1>
          {exitText}
          {displayName}
        </MobileExitTextH1>

        <MobileExitHelpTextH2>{exitHelpText}</MobileExitHelpTextH2>

        <MobileShopLogoImgContainer>
          <GenericImg src={resolvedShopLogoUrl} alt={mediumImageAltText || ''} />
        </MobileShopLogoImgContainer>

        <InterstitialModal
          actionButtons={ActionButtons}
          showModal={isInterruptShown}
          closeHandler={handleResumeJourney}
        >
          <h1 className="modal-header-text">{interruptHeaderText}</h1>
          {/* eslint-disable react/no-danger -- CMS supplies the shop's formatted information. */}
          <div
            dangerouslySetInnerHTML={{
              __html: importantInformation || '',
            }}
          />
          {/* eslint-enable react/no-danger */}
        </InterstitialModal>
        <MilesOffer
          className="interstitial"
          multiplier={multiplier}
          siteWideMultiplier={siteWideMultiplier}
          baseMiles={baseMiles}
          amountPerBaseMiles={amountPerBaseMiles}
          singularMilesText={singularMilesText}
          pluralMilesText={pluralMilesText}
          singularFlatRateMilesText={singularFlatRateMilesText}
          pluralFlatRateMilesText={pluralFlatRateMilesText}
          milesBadgeText={milesBadgeText}
          locale={locale}
        />
        <DotsLoader />

        <Button
          variant="primary"
          size="medium"
          gutter={6}
          id={track.dataTrackingId}
          data-track-id={track.dataTrackingId}
          data-track-click={dataTrack}
          onClick={this.redirectToExternalLink}
          buttonText={exitButtonText}
        />
      </InterstitialContainerDiv>
    );

    const renderComp = (
      <MaxWidthLayout>
        <MediaQueryHelper
          desktopChildren={desktopInterstitialPage}
          mobileChildren={mobileInterstitialPage}
        />
      </MaxWidthLayout>
    );
    return renderComp;
  }
}

export default InterstitialPage;
